<!-- fork overlay: fork-owned component (conversation pipeline kanban) — no OSS counterpart. -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PipelineCard from 'dashboard/components-next/Pipeline/PipelineCard.vue';

const props = defineProps({
  stage: {
    type: String,
    required: true,
  },
  stages: {
    type: Array,
    required: true,
  },
  conversations: {
    type: Array,
    required: true,
  },
  allCount: {
    type: Number,
    default: 0,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits([
  'cardDropped',
  'menuMove',
  'remove',
  'loadMore',
  'dragStart',
  'dragEnd',
]);

const { t } = useI18n();

const hasMore = computed(() => props.conversations.length < props.allCount);
const isEmpty = computed(() => !props.conversations.length && !props.isLoading);

// vuedraggable mutates the bound arrays on drop; only the target column's
// `added` event triggers the API write (handled by the board).
const onChange = event => {
  if (event.added) {
    emit('cardDropped', {
      conversation: event.added.element,
      toStage: props.stage,
    });
  }
};
</script>

<template>
  <section
    class="flex flex-col w-[19rem] max-h-full shrink-0 min-h-0 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
    :aria-label="stage"
  >
    <header class="flex items-center gap-2 px-3 py-2.5 shrink-0">
      <h2 class="text-sm font-medium truncate text-n-slate-12">{{ stage }}</h2>
      <span
        class="px-1.5 py-0.5 text-xs text-center rounded-full tabular-nums min-w-5 text-n-slate-11 bg-n-alpha-2"
      >
        {{ allCount }}
      </span>
      <Spinner v-if="isLoading" class="ltr:ml-auto rtl:mr-auto" />
    </header>
    <!-- sort=false: intra-column order is the server's last-activity sort,
         not user-defined — cards move BETWEEN columns only. -->
    <Draggable
      :list="conversations"
      group="pipeline"
      item-key="id"
      :sort="false"
      ghost-class="opacity-40"
      :animation="150"
      class="flex flex-col flex-1 min-h-24 gap-2 p-2 pt-0 overflow-y-auto"
      @change="onChange"
      @start="emit('dragStart')"
      @end="emit('dragEnd')"
    >
      <template #item="{ element }">
        <PipelineCard
          :conversation="element"
          :stages="stages"
          :current-stage="stage"
          :account-labels="accountLabels"
          @move="
            toStage => emit('menuMove', { conversation: element, toStage })
          "
          @remove="emit('remove', element)"
        />
      </template>
      <template #footer>
        <p
          v-if="isEmpty"
          class="px-3 py-6 text-xs text-center border border-dashed rounded-lg text-n-slate-10 border-n-strong"
        >
          {{ t('PIPELINE.COLUMN.EMPTY') }}
        </p>
        <!-- disabled while fetching: a load-more racing an in-flight page-1
             refresh would discard the authoritative page-1 response. -->
        <Button
          v-else-if="hasMore"
          :label="t('PIPELINE.COLUMN.LOAD_MORE')"
          variant="ghost"
          color="slate"
          size="sm"
          :is-loading="isLoading"
          :disabled="isLoading"
          class="w-full"
          @click="emit('loadMore')"
        />
      </template>
    </Draggable>
  </section>
</template>
